// src/components/reports/ReportModal.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { X, Flag, AlertTriangle } from "lucide-react";
import { createReport } from "../../redux/report/reportActions";
import { resetReportState } from "../../redux/report/reportSlice";

const REPORT_REASON_KEYS = [
  'inappropriate',
  'harassment',
  'spam',
  'hateSpeech',
  'violence',
  'copyright',
  'privacy',
  'other'
];

const ReportModal = ({ isOpen, onClose, target, type }) => {
  const { t } = useTranslation();
  const [reportReason, setReportReason] = useState("");
  const [description, setDescription] = useState("");

  const dispatch = useDispatch();
  const { loading, success, error } = useSelector((state) => state.reports) || {};

  const reportReasons = REPORT_REASON_KEYS.map((key) => t(`reportModal.reasons.${key}`));

  const handleSubmit = async () => {
    if (!reportReason) return;
    const payload = {
      target_type: type,     // backend's ReportCreate requires `target_type`, not `type` --
      target_id: target?.id, // every report submission 422'd before this fix.
      reason: reportReason,
      description,
    };
    dispatch(createReport(payload));
  };

  // Reset modal when closed or report success
  useEffect(() => {
    if (!isOpen) {
      setReportReason("");
      setDescription("");
      dispatch(resetReportState());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (success) {
      onClose?.();
    }
  }, [success, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
              <Flag className="text-red-400" size={20} />
              <span>{t('reportModal.title', { type })}</span>
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
            >
              <X size={18} />
            </button>
          </div>

          <div className="mb-6 p-4 bg-gray-900/50 rounded-xl border border-gray-700/50">
            <h3 className="font-medium text-white mb-2">
              {t('reportModal.reportingLabel', { name: target?.name || target?.title || t('reportModal.reportingFallback') })}
            </h3>
            <p className="text-gray-400 text-sm">
              {t('reportModal.intro')}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('reportModal.reasonLabel')}
              </label>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 text-white px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              >
                <option value="">{t('reportModal.selectReasonOption')}</option>
                {reportReasons.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('reportModal.detailsLabel')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 text-white px-3 py-2 rounded-xl h-24 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                placeholder={t('reportModal.detailsPlaceholder')}
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm">{t('reportModal.errorPrefix')} {error}</p>
            )}

            <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="text-amber-400 mt-0.5" size={16} />
                <div className="text-sm">
                  <p className="text-amber-300 font-medium mb-1">{t('reportModal.importantLabel')}</p>
                  <p className="text-amber-200/80">
                    {t('reportModal.falseReportsWarning')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-xl"
                disabled={loading}
              >
                {t('reportModal.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!reportReason || loading}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t('reportModal.submitting')}</span>
                  </>
                ) : (
                  <>
                    <Flag size={16} />
                    <span>{t('reportModal.submitReport')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
